import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid } from '@mui/material';
import { MarketButton, MarketLink, MarketPill } from '@market/react';
import { getLocaleString } from 'utilities/locales';
import { BUNDLE_OFFER_RESOLUTIONS } from 'constants/software';

// Inline `+` glyph icon. Square One spans verticals and isn't represented in
// SOFTWARE_GROUPS_BY_BASE_PRODUCT_KEY, so we don't reuse `getSoftwareTypeIcon`
// (which would return null). Matches the blockcell accessory-tile mock and
// keeps the bundle decoupled from the vertical-software icon system.
const PlusIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 5v14M5 12h14"
      stroke="#006AFF"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

// Format `monthly_cents` from the tier payload into a localized currency
// string. The view model emits cents with the standard ISO 4217 minor-unit
// precision (2 for USD/CAD/GBP, 0 for JPY). For v1 the bundle is US-only, so
// we treat monthly_cents as USD cents (precision = 2). When non-decimal-2
// currencies become reachable (post-launch), swap to precision-aware
// conversion via toMinorCurrencyUnit's inverse.
//
// We bypass the shared `toCurrency` helper because it always emits two
// fraction digits for USD ("$49.00") — the blockcell mock and design intent
// is to drop trailing zeros on whole-dollar amounts ("$49"). Mirrors the
// Safari narrowSymbol fallback from utilities/currency.js.
const formatTierMonthlyPrice = (tier) => {
  if (tier?.monthly_cents == null || !tier?.currency) return null;
  const dollars = tier.monthly_cents / 100;
  const minimumFractionDigits = tier.monthly_cents % 100 === 0 ? 0 : 2;
  const options = {
    style: 'currency',
    currency: tier.currency,
    minimumFractionDigits,
    ...(tier.currency === 'CAD' && { currencyDisplay: 'narrowSymbol' }),
  };
  try {
    return new Intl.NumberFormat(getLocaleString(), options).format(dollars);
  } catch {
    return new Intl.NumberFormat(getLocaleString(), {
      ...options,
      currencyDisplay: 'symbol',
    }).format(dollars);
  }
};

const BundleOfferCard = ({ offer }) => {
  const { t } = useTranslation();

  if (
    !offer ||
    offer.resolution === BUNDLE_OFFER_RESOLUTIONS.SUPPRESS ||
    offer.resolution === BUNDLE_OFFER_RESOLUTIONS.INELIGIBLE_TO_MIGRATE ||
    !offer.recommended_tier
  ) {
    // TODO: SAAS-4858b — render special "Square One coming soon" card for
    // resolution === 'ineligible_to_migrate'.
    return null;
  }

  const tier = offer.tiers?.[offer.recommended_tier];
  if (!tier) return null;

  const priceLabel = formatTierMonthlyPrice(tier);
  const trialDays = tier.trial_days;
  const hasTrial = typeof trialDays === 'number' && trialDays > 0;

  const trialLine = hasTrial ? t('cart.bundleOffer.trialDays', { trialDays }) : null;
  const priceLine =
    priceLabel != null
      ? hasTrial
        ? t('cart.bundleOffer.thenPrice', { price: priceLabel })
        : t('cart.bundleOffer.priceOnly', { price: priceLabel })
      : null;

  return (
    <Box px={2} py={3} data-testid="bundle-offer-card">
      <Grid container alignItems="center" spacing={2} wrap="nowrap">
        {/* Icon column — fixed-width tile, narrow gutter */}
        <Grid item xs="auto">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PlusIcon />
          </Box>
        </Grid>

        {/* Text column — pill, title, inline description+link */}
        <Grid item xs>
          <MarketPill
            variant="success"
            size="small"
            style={{
              marginBottom: 8,
              alignSelf: 'flex-start',
              cursor: 'default',
            }}
          >
            {t('cart.bundleOffer.recommended')}
          </MarketPill>
          <p className="medium-30">
            {t('cart.bundleOffer.title', { label: tier.label })}
          </p>
          <p className="paragraph-20">
            {t('cart.bundleOffer.description')}{' '}
            <MarketLink
              className="medium-20"
              onClick={() => {
                // TODO: SAAS-4858b — open Compare Plans modal.
              }}
            >
              {t('cart.bundleOffer.seeAllPlans')}
            </MarketLink>
          </p>
        </Grid>

        {/* Price + CTA column — flex column, hugs content */}
        <Grid item xs="auto">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={1}
          >
            {trialLine && (
              <p className="medium-20" style={{ margin: 0 }}>
                {trialLine}
              </p>
            )}
            {priceLine && (
              <p
                className="paragraph-20"
                style={{ margin: 0, color: '#7B7B7B' }}
              >
                {priceLine}
              </p>
            )}
            <MarketButton
              rank="secondary"
              onClick={() => {
                // TODO: SAAS-4858b — wire to cartClient.updateQuantity to swap
                // to recommended-tier variant_token.
              }}
            >
              {t('cart.bundleOffer.addCta')}
            </MarketButton>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BundleOfferCard;
